import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Form, Input, message, Spin } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { checkVAT, countries } from "jsvat-next";
import { CustomerAddressForm } from "./CustomerAddressForm";
import { getUserId, showError } from "../../utils";
import Api from "../../api";

type Props = {
  onSuccess?: () => void;
};
export const BillingInformationForm = (props: Props) => {
  const [defaults, setDefaults] = useState();
  const [countryCode, setCountryCode] = useState<string>();
  const [disableVAT, setDisableVAT] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  useEffect(() => {
    const getBillingInfo = async () => {
      try {
        const userId = getUserId();
        const values = await Api.get(`/api/users/${userId}/billing_info`);
        setDefaults(values);
        setCountryCode(values.country_code);
      } catch (error) {
        showError("Failed to get current billing information", error);
      }
    };

    getBillingInfo();
  }, []);

  const updateBillingInfo = async (values: any) => {
    const userId = getUserId();
    setIsUpdating(true);
    if (disableVAT) values.vat_number = "";
    try {
      await Api.patch(`/api/users/${userId}`, values);
      message.success("Updated billing information!");
      if (props.onSuccess) props.onSuccess();
    } catch (error) {
      showError("Failed to update billing info", error);
    }
    setIsUpdating(false);
  };
  const isInEU = useMemo(() => isCountryInEU(countryCode), [countryCode]);
  if (!defaults) return <Spin />;
  const onChangeCountry = (countryCode: string | undefined) => {
    setCountryCode(countryCode);
  };
  return (
    <Form
      initialValues={defaults}
      name="billing"
      layout="vertical"
      requiredMark="optional"
      onFinish={updateBillingInfo}
      className="billing-form"
    >
      <h3 className="section-card-subheading">Company</h3>
      <div className="billing-form-grid">
        <Form.Item
          label="Company Name"
          name="company_name"
          rules={[
            { required: true, message: "Please enter your company name" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Company Website (optional)" name="company_website">
          <Input placeholder="https://example.com" />
        </Form.Item>
      </div>

      <Form.Item
        label="VAT Number"
        name="vat_number"
        extra="Required for EU customers. If not applicable, enter 'N/A'."
        rules={[
          {
            required: isInEU && !disableVAT,
            message: "VAT is required in EU",
          },
          {
            validator: async (_, value) => {
              if (!isInEU || disableVAT) return Promise.resolve();
              if (!isValidVATNumber(value))
                return Promise.reject("Please enter a valid VAT number");
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input disabled={disableVAT} placeholder="e.g. BE0123456789" />
      </Form.Item>
      {isInEU && (
        <Form.Item className="billing-form-checkbox-row">
          <Checkbox
            checked={disableVAT}
            onChange={(e) => setDisableVAT(e.target.checked)}
          >
            I don't have a valid VAT number{" "}
            <span className="billing-form-checkbox-note">
              (you will be charged 21% VAT)
            </span>
          </Checkbox>
        </Form.Item>
      )}

      <h3 className="section-card-subheading" style={{ marginTop: 28 }}>
        Address
      </h3>
      <CustomerAddressForm
        onChangeCountry={onChangeCountry}
        initialValues={defaults}
      />

      <div className="billing-form-actions">
        <Button
          loading={isUpdating}
          icon={<SaveOutlined />}
          type="primary"
          htmlType="submit"
          size="large"
        >
          Save Changes
        </Button>
      </div>
    </Form>
  );
};
function isCountryInEU(countryCode: string | undefined): boolean {
  if (!countryCode) return false;
  const euCountryCodes = [
    "AT",
    "BE",
    "BG",
    "CY",
    "CZ",
    "DE",
    "DK",
    "EE",
    "ES",
    "FI",
    "FR",
    "GR",
    "HR",
    "HU",
    "IE",
    "IT",
    "LT",
    "LU",
    "LV",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SE",
    "SI",
    "SK",
    "SE",
    "IE",
  ];
  return euCountryCodes.includes(countryCode.toUpperCase());
}
function isValidVATNumber(vatNumber: string): boolean {
  return checkVAT(vatNumber, countries).isValid;
}
